import { ScheduleStatus } from "model/Schedule";
import type { ResolutionDetails } from "services/api/company/schedule.service";
import type {
  ScheduleApiShiftItem,
  ScheduleApiWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";

export interface DayTeam {
  readonly id: number;
  readonly name: string;
  readonly shifts: readonly DayShift[];
}

export interface DayShift {
  readonly details: readonly ResolutionDetails[];
  readonly endTime: string;
  readonly isUnassigned: boolean;
  readonly key: string;
  readonly role: string;
  readonly status: ScheduleStatus;
  readonly startTime: string;
  readonly workerExternalId: string | null;
  readonly workerName: string | null;
}

interface GroupScheduleDayShiftsParams {
  readonly rows: ScheduleApiWorkerRow[];
}

interface ShiftOccurrence {
  readonly item: ScheduleApiShiftItem;
  readonly row: ScheduleApiWorkerRow;
}

interface MutableDayTeam {
  id: number;
  name: string;
  shifts: DayShift[];
}

function toShiftKey(item: ScheduleApiShiftItem): string {
  return `${item.gigId}:${item.gigShiftId}`;
}

function findUnassignedShift(
  occurrences: readonly ShiftOccurrence[],
): ShiftOccurrence | null {
  return (
    occurrences.find(
      ({ item, row }) => !row.isWorker && item.status === ScheduleStatus.OPEN,
    ) ??
    occurrences.find(
      ({ item, row }) =>
        !row.isWorker && item.status === ScheduleStatus.PENDING,
    ) ??
    occurrences.find(
      ({ item, row }) =>
        !row.isWorker && item.status === ScheduleStatus.OFFERED,
    ) ??
    null
  );
}

/**
 * Merge backend representations of the same shift into one list item.
 * Assigned worker data takes precedence, while only scheduled unassigned
 * public and team shifts remain visible.
 */
function mergeShiftOccurrences(
  occurrences: readonly ShiftOccurrence[],
): DayShift | null {
  const worker = occurrences.find(
    ({ item, row }) => row.isWorker && item.status !== ScheduleStatus.WITHDRAWN,
  );
  const unassigned = findUnassignedShift(occurrences);
  const identity = worker ?? unassigned;
  if (!identity) return null;
  const isUnassigned = !worker;
  return {
    details: identity.item.details,
    endTime: identity.item.endTime,
    isUnassigned,
    key: toShiftKey(identity.item),
    role: isUnassigned
      ? (identity.row.text2?.trim() ?? "")
      : (identity.row.text?.trim() ?? ""),
    startTime: identity.item.startTime,
    status: identity.item.status,
    workerExternalId: identity.row.externalId,
    workerName: isUnassigned ? null : identity.row.heading.trim(),
  };
}

function groupOccurrencesByShift(
  rows: readonly ScheduleApiWorkerRow[],
): ReadonlyMap<string, readonly ShiftOccurrence[]> {
  const groups = new Map<string, ShiftOccurrence[]>();
  rows.forEach((row) => {
    row.items.forEach((item) => {
      const key = toShiftKey(item);
      const group = groups.get(key);
      if (group) {
        group.push({ item, row });
      } else {
        groups.set(key, [{ item, row }]);
      }
    });
  });
  return groups;
}

export function groupScheduleDayShifts({
  rows,
}: GroupScheduleDayShiftsParams): DayTeam[] {
  const teams = new Map<number, MutableDayTeam>();
  groupOccurrencesByShift(rows).forEach((occurrences) => {
    const shift = mergeShiftOccurrences(occurrences);
    if (!shift) return;
    const item = occurrences[0].item;
    const team = teams.get(item.workGroupId) ?? {
      id: item.workGroupId,
      name: item.workGroupName,
      shifts: [],
    };
    team.shifts.push(shift);
    teams.set(item.workGroupId, team);
  });
  return Array.from(teams.values());
}
